/* global globalThis */
import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectFinalRound,
  selectGameType,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectPlayers,
  selectPolicyMarket,
  selectRound,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {addNewPolicyToPolicyMarket, buyPolicy, clearPolicyMarket} from "../../../redux/actions";
import {calcPolicyPrice} from "../../policy/calcPolicyPrice";
import {getRandomPolicyForMarket} from "../../policy/getRandomPolicyForMarket";
import {getNewPolicyMarket} from "../../policy/getNewPolicyMarket";
import {POLICY_IDS_IMPLEMENTED} from "../../../configs/policies";
import {PHASES} from "../../../constants/phases";
import {calcUsedConstructionActions} from "../../gameState/construction/calcUsedConstructionActions";
import {getFactionPreferences} from "../strategy/factionPreferences";
import {getBotStrategy} from "../strategy/botStrategyStore";
import {getBotProfile} from "../strategy/getBotProfile";
import {POLICIES} from "../../../constants/policies";
import {LOW_RATING_ADVANTAGE_FORBIDDEN_BOT_POLICIES} from "../../../configs/lowRatingAdvantage";
import {
  BOT_EARLY_FORBIDDEN_POLICIES,
  BOT_EARLY_FORBIDDEN_POLICIES_LAST_ROUND,
} from "../../../configs/botEarlyForbiddenPolicies";
import {checkIfFinalRound, getRoundLimit} from "../../checkIfFinalRound";
import {calcW39GiantsPolicyCoinReserve} from "../factions/giantsBot";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {BOT_WEIGHTS} from "../botConfig";
import {isHegemonismBonusHexKey} from "../../policy/hegemonism/isHegemonismBonusHexKey";
import {getEnemyQuarterHexKeys, getOwnQuarterHexKeys} from "../evaluation/cities";
import {calcPlayerArmyStrength, calcStrongestOpponentArmy, findOwnCitiesUnderThreat} from "../evaluation/armyStrength";
import {getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {keyToCoords} from "../../coords/keyToCoords";
import {
  isPrePolicyWhitelistPlayer,
  isPreW35ArmyReservePlayer,
  isPreW36MarketRefreshPlayer,
  isPreW36PolicyReplacePlayer,
  isPreW36PolicyTargetsPlayer,
  isPreW36RefreshOverWeakPlayer,
} from "../abTestGates";
import {checkIfW36Feature} from "../evaluation/goldPlus";
import {getW36GoldTopPolicies, getW36GoldTopPoliciesOutsideWhitelist} from "../../../configs/botGoldPlus";
import {
  getW36MarketRefreshLastRound,
  getW36PolicyReplaceLastRound,
  getW36RefreshOverWeakLastRound,
  W36_MARKET_REFRESH_MIN_ACTIONS_LEFT,
  W36_MARKET_REFRESH_MIN_AFFORDABLE_SLOT_INDEX,
  W36_MARKET_REFRESH_REASONS,
} from "../../../configs/botPolicyMarket";
import {calcNoPolicyPriceRelief} from "./calcNoPolicyPriceRelief";
import {getBotBonusProfile} from "../getBotBonusProfile";
import {getBonusAxis} from "../strategy/bonusAxis";
import {calcBonusArmyPhaseCoinReserve, isBonusArmyPhase} from "../../../configs/botBonusBehavior";
import {calcArmyPhaseReserveWithBuildBudget, resolveConstructionPhaseCoins} from "../../../configs/botBuildRisk";
import {getPreferredUnitPrice} from "./decideRecruitmentStep";

// Олигархия-опенинг (живой тест 16.11): фракции с сильными гильдиями берут
// дешевую OLIGARCHY в 1-м раунде с решающим приоритетом
const OLIGARCHY_OPENING_FACTIONS = new Set([
  FACTIONS.VENTORY, FACTIONS.ENLIGHTENED, FACTIONS.ELEMENTALS,
]);
const OLIGARCHY_OPENING_MAX_PRICE = 3;
const OLIGARCHY_OPENING_BOOST = 12;

// Волна 18 (живой тест 17.5): вентори/элементали под угрозой берут MILITARISM/
// BARBARISM для обороны ближним боем
const WAVE18_MELEE_DEFENSE_FACTIONS = new Set([FACTIONS.VENTORY, FACTIONS.ELEMENTALS]);

// Поздняя игра для крысолюдов: ферменная экономика уже развита, буст AGRARIANISM не нужен
const RATMEN_AGRARIANISM_LATE_ROUND = 9;
const RATMEN_DEVELOPED_FARMS_COUNT = 5;

// FORTIFICATIONISM на старте не нужен никому
const FORTIFICATIONISM_EARLY_ROUNDS = 4;
// FANATICISM при 2+ руинах — приоритет для любой фракции (волна 23, запечена)
const FANATICISM_RUINS_PRIORITY_COUNT = 2;

// Волна 24 (policy_report_w23_by_winrate, 7000 партий): рекомендованные политики =
// топ-5 по винрейту фракции (порядок ранее рекомендованных сохранен, новые добавлены
// в порядке винрейта); антирекомендации = винрейт ниже -3 п.п. от общего винрейта
// фракции + прежние антирекомендации без данных о покупках. Запечено безусловно
// по итогам отчета №21 (+3.0 п.п. общего винрейта, ни одна фракция не в минусе)
const WAVE24_POLICY_PREFERENCES = {
  [FACTIONS.BEASTMEN]: [
    POLICIES.BARBARISM, POLICIES.ECOANARCHISM, POLICIES.HEGEMONISM,
    POLICIES.FANATICISM, POLICIES.ISOLATIONISM,
  ],
  [FACTIONS.DEMONS]: [
    POLICIES.HEGEMONISM, POLICIES.ECOANARCHISM, POLICIES.AGRARIANISM,
    POLICIES.FANATICISM, POLICIES.INDUSTRIALISM,
  ],
  [FACTIONS.DWARVES]: [
    POLICIES.SERVARIANISM, POLICIES.HEGEMONISM, POLICIES.FANATICISM,
    POLICIES.BARBARISM, POLICIES.TOTALITARIANISM,
  ],
  [FACTIONS.ELVES]: [
    POLICIES.HEGEMONISM, POLICIES.FANATICISM, POLICIES.BARBARISM,
    POLICIES.DESPOTISM, POLICIES.THEOCRACY,
  ],
  // Топ-5 по винрейту также включал THEOCRACY/TOTALITARIANISM, но обе ниже
  // -3 п.п. к общему винрейту — исключены как крайне не рекомендованные
  [FACTIONS.ENLIGHTENED]: [
    POLICIES.FANATICISM, POLICIES.OLIGARCHY, POLICIES.SERVARIANISM,
  ],
  [FACTIONS.HUMANS]: [
    POLICIES.FANATICISM, POLICIES.ECOANARCHISM, POLICIES.HEGEMONISM,
    POLICIES.BARBARISM, POLICIES.TOTALITARIANISM,
  ],
  [FACTIONS.INSECTOIDS]: [
    POLICIES.BARBARISM, POLICIES.TOTALITARIANISM, POLICIES.DESPOTISM,
    POLICIES.HEGEMONISM, POLICIES.FANATICISM,
  ],
  [FACTIONS.LEPRECHAUNS]: [
    POLICIES.AGRARIANISM, POLICIES.HEGEMONISM, POLICIES.FANATICISM,
    POLICIES.SERVARIANISM, POLICIES.BARBARISM,
  ],
  [FACTIONS.ORCS]: [
    POLICIES.TOTALITARIANISM, POLICIES.AGRARIANISM, POLICIES.HEGEMONISM,
    POLICIES.ECOANARCHISM, POLICIES.FANATICISM,
  ],
  [FACTIONS.VENTORY]: [
    POLICIES.OLIGARCHY, POLICIES.HEGEMONISM, POLICIES.FANATICISM,
    POLICIES.DESPOTISM, POLICIES.TOTALITARIANISM,
  ],
  [FACTIONS.ELEMENTALS]: [
    POLICIES.OLIGARCHY, POLICIES.BARBARISM, POLICIES.HEGEMONISM,
    POLICIES.AGRARIANISM, POLICIES.FANATICISM,
  ],
  [FACTIONS.RATMEN]: [
    POLICIES.ECOANARCHISM, POLICIES.FANATICISM, POLICIES.HEGEMONISM,
    POLICIES.INDUSTRIALISM, POLICIES.DESPOTISM,
  ],
};
const WAVE24_ANTI_POLICIES = {
  [FACTIONS.BEASTMEN]: [
    POLICIES.MILITARISM, POLICIES.AGRARIANISM, POLICIES.DESPOTISM,
    POLICIES.TOTALITARIANISM, POLICIES.INDUSTRIALISM, POLICIES.PATRIOTISM,
    POLICIES.OLIGARCHY, POLICIES.SERVARIANISM, POLICIES.FORTIFICATIONISM, POLICIES.THEOCRACY,
  ],
  [FACTIONS.DEMONS]: [
    POLICIES.BARBARISM, POLICIES.ISOLATIONISM, POLICIES.PATRIOTISM,
    POLICIES.SERVARIANISM, POLICIES.FORTIFICATIONISM, POLICIES.THEOCRACY, POLICIES.OLIGARCHY,
  ],
  [FACTIONS.DWARVES]: [
    POLICIES.AGRARIANISM, POLICIES.MILITARISM, POLICIES.INDUSTRIALISM,
    POLICIES.ISOLATIONISM, POLICIES.OLIGARCHY, POLICIES.PATRIOTISM, POLICIES.ECOANARCHISM,
    POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.ELVES]: [
    POLICIES.INDUSTRIALISM, POLICIES.PATRIOTISM, POLICIES.ISOLATIONISM,
    POLICIES.SERVARIANISM, POLICIES.OLIGARCHY, POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.ENLIGHTENED]: [
    POLICIES.THEOCRACY, POLICIES.TOTALITARIANISM, POLICIES.HEGEMONISM,
    POLICIES.BARBARISM, POLICIES.ISOLATIONISM, POLICIES.PATRIOTISM, POLICIES.AGRARIANISM,
    POLICIES.MILITARISM, POLICIES.DESPOTISM, POLICIES.INDUSTRIALISM, POLICIES.ECOANARCHISM,
    POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.HUMANS]: [
    POLICIES.MILITARISM, POLICIES.FORTIFICATIONISM, POLICIES.SERVARIANISM,
    POLICIES.PATRIOTISM, POLICIES.ISOLATIONISM,
  ],
  [FACTIONS.INSECTOIDS]: [
    POLICIES.MILITARISM, POLICIES.ECOANARCHISM, POLICIES.INDUSTRIALISM,
    POLICIES.THEOCRACY, POLICIES.AGRARIANISM, POLICIES.PATRIOTISM,
    POLICIES.OLIGARCHY, POLICIES.SERVARIANISM, POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.LEPRECHAUNS]: [
    POLICIES.PATRIOTISM, POLICIES.MILITARISM, POLICIES.DESPOTISM,
    POLICIES.ISOLATIONISM, POLICIES.THEOCRACY, POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.ORCS]: [
    POLICIES.INDUSTRIALISM, POLICIES.PATRIOTISM, POLICIES.OLIGARCHY, POLICIES.ISOLATIONISM,
    POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.VENTORY]: [
    POLICIES.MILITARISM, POLICIES.PATRIOTISM, POLICIES.INDUSTRIALISM,
    POLICIES.AGRARIANISM, POLICIES.ISOLATIONISM, POLICIES.ECOANARCHISM,
    POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.ELEMENTALS]: [
    POLICIES.MILITARISM, POLICIES.PATRIOTISM, POLICIES.ISOLATIONISM,
    POLICIES.DESPOTISM, POLICIES.INDUSTRIALISM, POLICIES.ECOANARCHISM,
    POLICIES.FORTIFICATIONISM,
  ],
  [FACTIONS.RATMEN]: [
    POLICIES.ISOLATIONISM, POLICIES.MILITARISM, POLICIES.TOTALITARIANISM,
    POLICIES.PATRIOTISM, POLICIES.OLIGARCHY, POLICIES.THEOCRACY,
    POLICIES.SERVARIANISM, POLICIES.FORTIFICATIONISM,
  ],
};
// Волна 27 пробовала расширить сжатый список просвещенных двумя нейтральными
// политиками (INDUSTRIALISM/AGRARIANISM, вотч №1 отчета №22: 6.4% партий без
// политик из-за пустого рынка) — ОТКАЧЕНО по итогам отчета №24: слабые политики
// «залипали» через порог замены и вытесняли сильные OLIGARCHY/SERVARIANISM
// (винрейт −6.9 п.п. за CI, ср. очки −8.4). Пустой рынок решать точечно
// (покупка нейтральной только при пустом рынке сильных с позднего раунда,
// либо обнуление порога замены для перехода «вне-списка -> топ списка»)

// Волна 23 (22.2): свои руины в радиусе 1 от центра поля — повод для HEGEMONISM
const HEGEMONISM_CENTRAL_RUINS_BOOST = 8;
const HEGEMONISM_CENTRAL_RUINS_DISTANCE = 1;
const FANATICISM_RUINS_OWNED_BOOST = 8;
// Гномы: SERVARIANISM покупается даже за 3-4 монеты; при доходе 18+ — удерживается
const DWARVES_SERVARIANISM_BOOST = 4;
const DWARVES_SERVARIANISM_HOLD_INCOME = 18;

// Ценность политики по позиции в списке предпочтений фракции (убывает с рангом)
// Волна 30 (policy_report_w30, 7000 партий прод-конфигурации: волна 28 +
// сильный режим по пофракционным гейтам, планировщик выключен): ЖЕСТКИЙ
// вайтлист покупок. Правило отбора (решение пользователя): берутся политики с
// ПОЛОЖИТЕЛЬНОЙ дельтой к общему винрейту фракции, максимум 6 на фракцию; если
// положительных меньше 4 — список добирается наименее отрицательными.
// Исключение — ЛЮДИ: им топ-10 по винрейту (см. комментарий у их списка).
// В скобках — дельта и число партий-покупок из отчета.
// Все прочие политики бот не покупает вовсе (calcPolicyValue возвращает 0).
// Экспорт — для метрик харнесса (какая покупка из вайтлиста), решения читают
// список только здесь
export const WAVE30_POLICY_WHITELIST = {
  [FACTIONS.BEASTMEN]: [
    POLICIES.HEGEMONISM, // +8.5 (n=127)
    POLICIES.BARBARISM, // +1.8 (n=1109)
    POLICIES.ECOANARCHISM, // -0.3 (n=891), добор до 4
    POLICIES.FANATICISM, // -0.3 (n=77), добор до 4
  ],
  [FACTIONS.DEMONS]: [
    POLICIES.HEGEMONISM, // +10.0 (n=477)
    POLICIES.DESPOTISM, // +6.7 (n=186)
    POLICIES.FANATICISM, // +6.0 (n=258)
    POLICIES.ECOANARCHISM, // +2.0 (n=1179)
    POLICIES.TOTALITARIANISM, // +0.6 (n=153)
  ],
  [FACTIONS.DWARVES]: [
    POLICIES.FANATICISM, // +5.1 (n=92)
    POLICIES.SERVARIANISM, // +0.9 (n=1555)
    POLICIES.THEOCRACY, // +0.2 (n=318)
    POLICIES.HEGEMONISM, // -1.3 (n=81), добор до 4
  ],
  [FACTIONS.ELEMENTALS]: [
    POLICIES.FANATICISM, // +30.8 (n=41 — малая выборка)
    POLICIES.HEGEMONISM, // +5.9 (n=84)
    POLICIES.BARBARISM, // +3.8 (n=927)
    POLICIES.SERVARIANISM, // +1.5 (n=171)
  ],
  [FACTIONS.ELVES]: [
    POLICIES.HEGEMONISM, // +11.3 (n=272)
    POLICIES.FANATICISM, // +11.2 (n=241)
    POLICIES.BARBARISM, // +5.8 (n=789)
    POLICIES.AGRARIANISM, // +4.4 (n=119)
  ],
  [FACTIONS.ENLIGHTENED]: [
    POLICIES.OLIGARCHY, // +5.3 (n=1055)
    POLICIES.FANATICISM, // +4.2 (n=405)
    POLICIES.SERVARIANISM, // -1.6 (n=969), добор до 4
    POLICIES.HEGEMONISM, // -4.0 (n=25), добор до 4
  ],
  // ЛЮДИ — исключение (отчет №27, решение пользователя): им разрешен топ-10
  // политик по винрейту, а не топ-6. Причина: сила фракции в ЧАСТОЙ
  // ситуативной смене политик (мгновенная активация + скидка на покупку), и
  // узкий список ее душит — в A/B волны 30 люди дали единственное заметное
  // падение по очкам (41.2 против 45.7 у прежнего поведения).
  // Ниже — все 10 политик, которые люди вообще покупали в прогоне 7000 партий;
  // прочие отсекаются анти-списком волны 24, как и раньше
  [FACTIONS.HUMANS]: [
    POLICIES.FANATICISM, // +6.8 (n=445)
    POLICIES.HEGEMONISM, // +4.2 (n=347)
    POLICIES.ECOANARCHISM, // +0.8 (n=1485)
    POLICIES.OLIGARCHY, // +0.5 (n=183)
    POLICIES.BARBARISM, // +0.1 (n=1085)
    POLICIES.AGRARIANISM, // -0.6 (n=163)
    POLICIES.TOTALITARIANISM, // -1.2 (n=837)
    POLICIES.DESPOTISM, // -2.6 (n=256)
    POLICIES.INDUSTRIALISM, // -3.5 (n=210)
    POLICIES.THEOCRACY, // -4.9 (n=209)
  ],
  [FACTIONS.INSECTOIDS]: [
    POLICIES.FANATICISM, // +15.4 (n=171)
    POLICIES.BARBARISM, // +2.6 (n=886)
    POLICIES.TOTALITARIANISM, // +0.3 (n=715)
    POLICIES.HEGEMONISM, // -0.1 (n=208), добор до 4
  ],
  [FACTIONS.LEPRECHAUNS]: [
    POLICIES.FANATICISM, // +15.2 (n=153)
    POLICIES.HEGEMONISM, // +9.3 (n=180)
    POLICIES.SERVARIANISM, // +4.8 (n=566)
    POLICIES.BARBARISM, // +2.7 (n=381)
    POLICIES.TOTALITARIANISM, // +1.7 (n=110)
  ],
  [FACTIONS.ORCS]: [
    POLICIES.FANATICISM, // +9.4 (n=101)
    POLICIES.HEGEMONISM, // +8.5 (n=174)
    POLICIES.ECOANARCHISM, // +8.2 (n=611)
    POLICIES.DESPOTISM, // +7.1 (n=135)
    POLICIES.SERVARIANISM, // +4.7 (n=100)
  ],
  [FACTIONS.RATMEN]: [
    POLICIES.BARBARISM, // +6.7 (n=92)
    POLICIES.ECOANARCHISM, // +6.3 (n=1310)
    POLICIES.FANATICISM, // +5.4 (n=226)
    POLICIES.AGRARIANISM, // +3.8 (n=512)
    POLICIES.HEGEMONISM, // +1.5 (n=211)
  ],
  [FACTIONS.VENTORY]: [
    POLICIES.FANATICISM, // +11.4 (n=147)
    POLICIES.BARBARISM, // +5.1 (n=107)
    POLICIES.HEGEMONISM, // +4.5 (n=106)
    POLICIES.OLIGARCHY, // +2.6 (n=1325)
  ],
};

const PREFERENCE_RANK_VALUES = [10, 8, 6.5, 5.5, 4.5, 4, 3.5];
// Базовая ценность политики вне списка предпочтений
const NON_PREFERRED_VALUE = 2;
// Насколько новая политика должна превосходить текущую, чтобы менять ее
const POLICY_REPLACEMENT_THRESHOLD = 3;

// Бот, засидевшийся БЕЗ политики: с 4-го раунда раунд за раундом все охотнее
// переплачивает за разрешенную ему политику и все меньше бережет накопления на
// стройцель (см. calcNoPolicyPriceRelief — там же замер и обоснование). Потолок
// равен максимальной цене слота рынка: с этого момента проходит любой слот с
// ненулевой ценностью, а резерв под стройку не мешает вовсе
const NO_POLICY_RELIEF_START_ROUND = 4;
const NO_POLICY_RELIEF_PER_ROUND = 1;
const NO_POLICY_RELIEF_MAX = 6;

// Аффинити политик к архетипам: военные политики агрессорам, экономические экономистам
const MILITARY_POLICIES = new Set([
  POLICIES.MILITARISM, POLICIES.DESPOTISM, POLICIES.TOTALITARIANISM, POLICIES.PATRIOTISM,
  POLICIES.BARBARISM, POLICIES.FORTIFICATIONISM, POLICIES.HEGEMONISM,
]);
const ECONOMIC_POLICIES = new Set([
  POLICIES.INDUSTRIALISM, POLICIES.OLIGARCHY, POLICIES.THEOCRACY, POLICIES.SERVARIANISM,
  POLICIES.AGRARIANISM, POLICIES.FANATICISM,
]);

/**
 * Волна 36 (PT, PX): доля оставшейся партии, в которой подействует политика,
 * купленная сейчас ЗАМЕНОЙ действующей. У не-людей замена вступает с конца
 * раунда (редьюсер BUY_POLICY кладет ее в nextPolicy), у людей — сразу. Из
 * текущего и оставшихся раундов замена работает только в оставшихся: при
 * лимите 13 в R8 это 5/6, в R12 — 1/2. Без лимита раундов — 1.
 */
const calcReplacementEffectShare = (state, player) => {
  if (isPlayerFaction(player, FACTIONS.HUMANS)) {
    return 1;
  }
  const roundLimit = getRoundLimit(selectFinalRound(state));
  if (roundLimit === null) {
    return 1;
  }
  const roundsAfterCurrent = roundLimit - selectRound(state);
  return roundsAfterCurrent > 0 ? roundsAfterCurrent / (roundsAfterCurrent + 1) : 0;
};

// Почему оценка рынка не выбирает ничего еще до просмотра слотов
// (evaluateBotPolicyMarket): обновлять рынок в этих случаях бессмысленно
export const BOT_POLICY_MARKET_BLOCK_REASONS = {
  // Политика в этой фазе уже куплена (nextPolicy назначена)
  NEXT_POLICY: 'next_policy',
  // Последний раунд: новая политика не успеет подействовать
  FINAL_ROUND: 'final_round',
  // Рынок пуст
  EMPTY_MARKET: 'empty_market',
  // Гномы держат «Серваризм» до конца партии
  DWARVES_SERVARIANISM_HOLD: 'dwarves_servarianism_hold',
};

const createBlockedPolicyMarketEvaluation = (blockReason) => ({blockReason, bestIndex: -1});

/**
 * Оценка рынка политик ботом текущего хода — общая для покупки
 * (findBotPolicyToBuy) и обновления рынка (волна 36). Только оценка, без
 * покупки и без записи в стратегию бота.
 *
 * Возвращает:
 *  - blockReason — причина, по которой выбирать не из чего еще до слотов
 *    (BOT_POLICY_MARKET_BLOCK_REASONS), или null; при ней остальных полей,
 *    кроме bestIndex = -1, нет;
 *  - bestIndex — слот, который бот купил бы сейчас, или -1;
 *  - buyableIndices — все слоты, которые бот готов купить (bestIndex — лучший);
 *  - spendableCoins — монеты на политику за вычетом резервов стройки и армии;
 *  - forbiddenPolicies — запреты покупки (фора новичкам, ранние раунды);
 *  - calcPolicyValue(policyId, price = null) — ценность политики для бота;
 *  - currentPolicyValue — ценность действующей политики с порогом замены
 *    (0 без политики);
 *  - goldTopPolicies — топ-3 фракции для ботов золото+ (configs/botGoldPlus),
 *    если у бота действует подчасть PT, иначе null.
 * Оценка: ранг в предпочтениях фракции + аффинити архетипа - цена (позиция на рынке + 1).
 */
export const evaluateBotPolicyMarket = (state) => {
  const currentPlayer = selectCurrentPlayer(state);

  // По правилам: 1 политика за фазу строительства (nextPolicy уже назначена — куплено)
  if (currentPlayer.nextPolicy) {
    return createBlockedPolicyMarketEvaluation(BOT_POLICY_MARKET_BLOCK_REASONS.NEXT_POLICY);
  }

  const round = selectRound(state);
  if (checkIfFinalRound(state)) {
    // в последнем раунде новая политика не успеет подействовать
    return createBlockedPolicyMarketEvaluation(BOT_POLICY_MARKET_BLOCK_REASONS.FINAL_ROUND);
  }

  const policyMarket = selectPolicyMarket(state);
  if (!policyMarket || policyMarket.length === 0) {
    return createBlockedPolicyMarketEvaluation(BOT_POLICY_MARKET_BLOCK_REASONS.EMPTY_MARKET);
  }

  // Гномы: SERVARIANISM с большим доходом монет удерживается до конца игры —
  // экономный эндшпиль с конвертацией накоплений в очки
  if (isPlayerFaction(currentPlayer, FACTIONS.DWARVES)
    && currentPlayer.policy?.id === POLICIES.SERVARIANISM
    && (currentPlayer.coinsIncome || 0) >= DWARVES_SERVARIANISM_HOLD_INCOME
  ) {
    return createBlockedPolicyMarketEvaluation(BOT_POLICY_MARKET_BLOCK_REASONS.DWARVES_SERVARIANISM_HOLD);
  }

  const strategy = getBotStrategy(currentPlayer.id);
  const profile = getBotProfile(state, currentPlayer);
  const preferences = getFactionPreferences(currentPlayer.factionId, currentPlayer.id);

  // Послабление боту, который до сих пор без политики (см. calcNoPolicyPriceRelief)
  const noPolicyRelief = calcNoPolicyPriceRelief(round, Boolean(currentPlayer.policy), {
    startRound: NO_POLICY_RELIEF_START_ROUND,
    perRound: NO_POLICY_RELIEF_PER_ROUND,
    max: NO_POLICY_RELIEF_MAX,
  });

  // Бюджет: не залезаем в накопления на строительную цель. Резерв уступает
  // политике тем сильнее, чем дольше бот сидит без нее
  const savingsReserve = strategy.savingsGoal ? strategy.savingsGoal.price : 0;
  // Волна 33 (C10): бот с пакетом «сильных городов» в армейской фазе держит
  // резерв на два отряда и при покупке политики — она покупается ДО построек,
  // а стройка тот же резерв уже не тратит (planConstructionChain). У обычных
  // ботов и legacy-группы A/B резерв 0 — бюджет прежний
  // Волна 35 (S, гейт isPreW35ArmyReservePlayer): тот же резерв с минимальным
  // бюджетом стройки, что и в planConstructionChain (см. configs/botBuildRisk)
  let bonusArmyPhaseReserve = 0;
  const bonusProfile = getBotBonusProfile(currentPlayer, selectGameType(state));
  if (bonusProfile?.strongCities && isBonusArmyPhase(round) && getBonusAxis(currentPlayer.id)) {
    const ownUnits = selectUnits(state).filter((unit) => unit.ownerId === currentPlayer.id);
    const preferredUnitPrice = Math.max(
      1, Math.min(8, getPreferredUnitPrice(currentPlayer, round, ownUnits, selectCitiesCache(state)))
    );
    bonusArmyPhaseReserve = calcBonusArmyPhaseCoinReserve(bonusProfile, ownUnits.length, preferredUnitPrice);
    if (!isPreW35ArmyReservePlayer(currentPlayer.id)) {
      // От монет на начало хода стройки (запись decideConstructionStep до покупки
      // политики): бюджет у политики и построек общий
      bonusArmyPhaseReserve = calcArmyPhaseReserveWithBuildBudget(
        bonusArmyPhaseReserve, preferredUnitPrice,
        resolveConstructionPhaseCoins(
          strategy.w35ConstructionPhaseCoins, round, currentPlayer, selectPendingBuildings(state)
        )
      );
    }
  }
  // Волна 39: пока у великанов нет великана, политика не съедает монеты на него
  const w39GiantsReserve = calcW39GiantsPolicyCoinReserve({
    player: currentPlayer,
    playerUnits: selectUnits(state).filter((unit) => unit.ownerId === currentPlayer.id),
    isFinalRound: checkIfFinalRound(state),
  });
  const spendableCoins = currentPlayer.coins - Math.max(savingsReserve - noPolicyRelief, 0) - bonusArmyPhaseReserve
    - w39GiantsReserve;

  const unitsForPolicies = selectUnits(state);
  const citiesCacheForPolicies = selectCitiesCache(state);

  // Угроза своим городам: FORTIFICATIONISM имеет смысл только под давлением
  const isUnderThreatForPolicies = findOwnCitiesUnderThreat(
    currentPlayer, unitsForPolicies, citiesCacheForPolicies, getBotGridRenderer(state)
  ).length > 0;

  // Люди: ожидаемый бой в этом раунде (оборона или свой отряд у чужого квартала) —
  // мгновенная активация позволяет купить военную политику прямо перед боем
  const isHumans = isPlayerFaction(currentPlayer, FACTIONS.HUMANS);
  let isBattleExpected = false;
  let hasMilitarySuperiority = false;
  if (isHumans) {
    const enemyQuarters = getEnemyQuarterHexKeys(citiesCacheForPolicies, currentPlayer.color);
    const gridRenderer = getBotGridRenderer(state);
    isBattleExpected = isUnderThreatForPolicies || unitsForPolicies.some((unit) =>
      unit.ownerId === currentPlayer.id
      && (enemyQuarters.has(unit.hexKey)
        || getNeighborHexKeys(unit.hexKey, gridRenderer).some((adjKey) => enemyQuarters.has(adjKey)))
    );
    const ownArmy = calcPlayerArmyStrength(currentPlayer.id, unitsForPolicies);
    hasMilitarySuperiority = ownArmy > calcStrongestOpponentArmy(
      currentPlayer.id, selectPlayers(state), unitsForPolicies
    );
  }

  // Зверолюды: паритет армии (90%+ сильнейшего противника) — время боевых политик
  let isBeastmenAtParity = false;
  if (isPlayerFaction(currentPlayer, FACTIONS.BEASTMEN)) {
    const beastmenArmy = calcPlayerArmyStrength(currentPlayer.id, unitsForPolicies);
    const strongestForBeastmen = calcStrongestOpponentArmy(
      currentPlayer.id, selectPlayers(state), unitsForPolicies
    );
    isBeastmenAtParity = beastmenArmy >= strongestForBeastmen * 0.9;
  }

  // Лепреконы: ISOLATIONISM ценен, когда рой уже крадет 3+ монеты за раунд
  // (первый ход после перехвата инициативы = кража до трат противника)
  let isLeprechaunIsolationismReady = false;
  if (isPlayerFaction(currentPlayer, FACTIONS.LEPRECHAUNS)) {
    const stealPotential = unitsForPolicies
      .filter((unit) => unit.ownerId === currentPlayer.id)
      .reduce((sum, unit) => sum + Math.floor(unit.size / 5), 0);
    isLeprechaunIsolationismReady = stealPotential >= 3;
  }

  // Крысолюды: AGRARIANISM критичен для ферменной армии — берем даже не самым
  // дешевым на рынке, кроме поздней игры с уже развитой экономикой ферм и складов
  let isRatmenAgrarianismBoostActive = false;
  if (isPlayerFaction(currentPlayer, FACTIONS.RATMEN) && round < RATMEN_AGRARIANISM_LATE_ROUND) {
    const playerCities = selectCitiesCache(state)?.[currentPlayer.color] || [];
    const farmsCount = playerCities.reduce(
      (sum, city) => sum + (city[COMMON_BUILDING_TOKENS.FARM] || []).length, 0
    );
    const warehousesCount = playerCities.reduce(
      (sum, city) => sum + (city[SPECIAL_BUILDING_TOKENS.WAREHOUSE] || []).length, 0
    );
    isRatmenAgrarianismBoostActive = !(farmsCount >= RATMEN_DEVELOPED_FARMS_COUNT && warehousesCount >= 1);
  }

  // Свои руины (для условий FANATICISM — его бонусы завязаны на руины)
  const ownCitiesForPolicies = selectCitiesCache(state)?.[currentPlayer.color] || [];
  const ownRuinsHexKeys = ownCitiesForPolicies.flatMap(
    (city) => [...(city.ruins || []), ...(city.super_ruins || [])]
  );
  const ownRuinsCount = ownRuinsHexKeys.length;

  // Волна 23 (22.2, запечена): свои руины в радиусе 1 от центра поля —
  // HEGEMONISM становится кандидатом даже для фракций с антирекомендацией
  const hasCentralRuins = ownRuinsHexKeys.some((hexKey) => {
    const [q, r] = keyToCoords(hexKey);
    return (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2 <= HEGEMONISM_CENTRAL_RUINS_DISTANCE;
  });

  // Волна 24 (запечена): анти-списки по винрейту (policy_report_w23_by_winrate)
  const effectiveAntiPolicies = WAVE24_ANTI_POLICIES[currentPlayer.factionId] || [];

  // Волна 30: вайтлист покупок фракции (см. WAVE30_POLICY_WHITELIST).
  // Фолбэк на прежние предпочтения — для фракций без данных прогона.
  // A/B: игрокам legacy-группы вайтлист не применяется (прежнее поведение)
  const factionPolicyWhitelist = isPrePolicyWhitelistPlayer(currentPlayer.id)
    ? null
    : (WAVE30_POLICY_WHITELIST[currentPlayer.factionId] || null);

  // Волна 36 (PT, боты золото+): топ-3 фракции (configs/botGoldPlus) — первые
  // в ранге ценности (10, 8, 6.5), за ними остальные предпочтения волны 24.
  // Вайтлист волны 30 действует и на топ-3; вне него покупается только
  // Теократия Вентори, гномов и элементалей (решение владельца 27.10, см.
  // W36_GOLD_TOP_POLICIES_OUTSIDE_WHITELIST). Ситуативные гейты ниже (Фанатизм
  // без руин, Гегемонизм без центра, Фортификационизм) и запреты покупки
  // (forbiddenPolicies) действуют как прежде. У великанов списка нет — для них
  // все по-старому. null — подчасть не действует, все как в main
  const goldTopPolicies = checkIfW36Feature(currentPlayer, selectGameType(state), isPreW36PolicyTargetsPlayer)
    ? getW36GoldTopPolicies(currentPlayer.factionId)
    : null;
  const isGoldTargetsActive = Boolean(goldTopPolicies && goldTopPolicies.length > 0);
  const goldOutsideWhitelistPolicies = isGoldTargetsActive
    ? getW36GoldTopPoliciesOutsideWhitelist(currentPlayer.factionId)
    : [];
  // Покупает ли бот политику в принципе: вайтлист волны 30 с исключениями
  // топ-3 золото+. Та же проверка решает, держит ли бот цель: политика топ-3,
  // которую он сам не купил бы (ее купил ИИ-игрок до ухода на автопилот или
  // админ при ручном управлении ботом), целью не считается — ни для покупки
  // цели в обход цены, ни для обновления рынка ради замены (PX)
  const checkIfPolicyWhitelisted = (policyId) => !factionPolicyWhitelist
    || factionPolicyWhitelist.includes(policyId)
    || goldOutsideWhitelistPolicies.includes(policyId);
  const goldPreferenceRanking = isGoldTargetsActive
    ? [
      ...goldTopPolicies,
      ...(WAVE24_POLICY_PREFERENCES[currentPlayer.factionId] || preferences.policies)
        .filter((policyId) => !goldTopPolicies.includes(policyId)),
    ]
    : null;

  // Фора новичкам: пока в партии есть игрок с рейтингом ниже порога, боты не
  // покупают политики из списка запрета — сейчас это «Экоанархизм» (см.
  // configs/lowRatingAdvantage). Признак лежит на самом игроке и действует до
  // конца партии; режим здесь знать не нужно — в обучении, кампании, демо и
  // партиях из одних ботов его не ставит никто
  const lowRatingAdvantageForbiddenPolicies = selectPlayers(state).some((player) => player.hasLowRatingAdvantage)
    ? LOW_RATING_ADVANTAGE_FORBIDDEN_BOT_POLICIES
    : [];

  // Начало партии: пока идет раунд не позже заданного (включительно), бот не
  // покупает политики из списка раннего запрета — сейчас это тоже
  // «Экоанархизм». Со следующего раунда запрет снят (см.
  // configs/botEarlyForbiddenPolicies). Запрет общий: вид бота, лигу, фракцию
  // и режим партии здесь не спрашиваем
  const earlyForbiddenPolicies = round <= BOT_EARLY_FORBIDDEN_POLICIES_LAST_ROUND
    ? BOT_EARLY_FORBIDDEN_POLICIES
    : [];

  const forbiddenPolicies = [...lowRatingAdvantageForbiddenPolicies, ...earlyForbiddenPolicies];

  const calcPolicyValue = (policyId, price = null) => {
    // Волна 30: жесткий вайтлист — все, чего нет в списке фракции, не
    // покупается вовсе. Проверка первой: перекрывает и ситуативные бусты,
    // и обход анти-списков ниже
    if (!checkIfPolicyWhitelisted(policyId)) {
      return 0;
    }

    // Волна 23 (22.2, запечена): руины у центра поля делают HEGEMONISM ситуативно
    // ценным даже фракциям с антирекомендацией — обходим анти-списки
    const isHegemonismCentralRuinsCase = policyId === POLICIES.HEGEMONISM && hasCentralRuins;

    // Антирекомендации фракции — не покупаем никогда. Волна 24 (запечена):
    // единый анти-список по винрейту заменил прежние (factionPreferences + волна 18)
    if (!isHegemonismCentralRuinsCase && effectiveAntiPolicies.includes(policyId)) {
      return 0;
    }

    // Живой тест 16.12: крысолюдам TOTALITARIANISM запрещен — армия из d6/d4
    // не получает бонус размера, покупка выброшена впустую
    if (policyId === POLICIES.TOTALITARIANISM
      && isPlayerFaction(currentPlayer, FACTIONS.RATMEN)
    ) {
      return 0;
    }

    // FORTIFICATIONISM: в ранней игре бесполезен всем, а покупается вообще
    // только когда своим городам реально угрожают (крепости — оборонный ответ)
    if (policyId === POLICIES.FORTIFICATIONISM
      && (round <= FORTIFICATIONISM_EARLY_ROUNDS || !isUnderThreatForPolicies)
    ) {
      return 0;
    }

    // FANATICISM без руин во владении не дает ничего;
    // с 3+ руинами — приоритетная покупка для любой фракции
    if (policyId === POLICIES.FANATICISM && ownRuinsCount === 0) {
      return 0;
    }

    // Волна 24 (запечена): списки предпочтений по винрейту для всех 12 фракций.
    // Волна 36 (PT): у ботов золото+ впереди топ-3 фракции
    const effectivePolicies = goldPreferenceRanking
      || WAVE24_POLICY_PREFERENCES[currentPlayer.factionId]
      || preferences.policies;
    const preferenceRank = effectivePolicies.indexOf(policyId);
    let value = preferenceRank >= 0
      ? (PREFERENCE_RANK_VALUES[preferenceRank] ?? 3)
      : NON_PREFERRED_VALUE;

    // Волна 23 (22.3/22.4, запечена): полный буст FANATICISM с 2 руин любой
    // фракции и с 1 руины просвещенным (конвертация очков + доход руин)
    const isEnlightenedFanaticismCase = isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED)
      && ownRuinsCount >= 1;
    if (policyId === POLICIES.FANATICISM
      && (ownRuinsCount >= FANATICISM_RUINS_PRIORITY_COUNT || isEnlightenedFanaticismCase)
    ) {
      value += FANATICISM_RUINS_OWNED_BOOST;
    } else if (policyId === POLICIES.FANATICISM && ownRuinsCount >= 1) {
      // Волна 20 (19.13): хороший винрейт у FANATICISM недооценен рынком —
      // раньше буст требовал 3+ руины, поднимаем приоритет уже с 1 руины
      value += FANATICISM_RUINS_OWNED_BOOST * 0.6;
    }

    // Волна 20 (19.14): агрессивные архетипы с 6 раунда сильно приоритизируют
    // ECOANARCHISM (снабжение без ферм освобождает стройку под казармы/армию);
    // особо усилено для demons/beastmen/orcs
    if (policyId === POLICIES.ECOANARCHISM && profile.military > 1 && round >= 6) {
      const isEcoanarchismCoreFaction = isPlayerFaction(currentPlayer, FACTIONS.DEMONS)
        || isPlayerFaction(currentPlayer, FACTIONS.BEASTMEN)
        || isPlayerFaction(currentPlayer, FACTIONS.ORCS);
      value += isEcoanarchismCoreFaction ? 10 : 6;
    }

    // Гномы: SERVARIANISM — ядро стратегии, берем даже за 3-4 монеты
    if (policyId === POLICIES.SERVARIANISM && isPlayerFaction(currentPlayer, FACTIONS.DWARVES)) {
      value += DWARVES_SERVARIANISM_BOOST;
    }

    // Олигархия-опенинг: дешевая OLIGARCHY в 1-м раунде с решающим приоритетом
    // (тыловой сетап и ранние гильдии уже настроены) (16.11)
    if (policyId === POLICIES.OLIGARCHY
      && round === 1
      && price !== null && price <= OLIGARCHY_OPENING_MAX_PRICE
      && OLIGARCHY_OPENING_FACTIONS.has(currentPlayer.factionId)
    ) {
      value += OLIGARCHY_OPENING_BOOST;
    }

    // Волна 18 (живой тест 17.5): вентори и элементали при активной угрозе
    // агрессора берут политики бонуса ближнего боя для обороны (factions_rules)
    if (isUnderThreatForPolicies
      && WAVE18_MELEE_DEFENSE_FACTIONS.has(currentPlayer.factionId)
      && (policyId === POLICIES.MILITARISM || policyId === POLICIES.BARBARISM)
    ) {
      value += 5;
    }

    // Лепреконы: ISOLATIONISM при потенциале кражи 3+ монет за раунд
    // (проба pb: смещение политик от фокуса плана планировщика давало РЕГРЕСС
    // +9.2 -> +4.1 п.п. и откачено — политики не трогаем, урок волны 26)
    if (policyId === POLICIES.ISOLATIONISM && isLeprechaunIsolationismReady) {
      value += 4;
    }

    // Люди: военные политики перед ожидаемым боем, ECOANARCHISM при превосходстве.
    // Бусты усилены по итогам отчета №6: ситуативная ценность должна решать смену
    if (isHumans) {
      if (isBattleExpected && MILITARY_POLICIES.has(policyId)) {
        value += 5;
      }
      if (policyId === POLICIES.ECOANARCHISM && hasMilitarySuperiority) {
        value += 5;
      }
    }

    // Зверолюды: выросшая армия при паритете+ берет боевую политику и атакует
    // (нюанс живого теста 12.4)
    if (isBeastmenAtParity
      && (policyId === POLICIES.BARBARISM || policyId === POLICIES.MILITARISM)
    ) {
      value += 4;
    }

    // Аффинити архетипа
    if (MILITARY_POLICIES.has(policyId)) {
      value *= profile.military;
    } else if (ECONOMIC_POLICIES.has(policyId)) {
      value *= profile.economy;
    }

    if (policyId === POLICIES.AGRARIANISM && isRatmenAgrarianismBoostActive) {
      value += BOT_WEIGHTS.RATMEN_AGRARIANISM_BOOST;
    }

    // Крысолюды с развитой ферменной экономикой: AGRARIANISM отработал свое —
    // обесцениваем, чтобы рой сменил его на боевую политику (MILITARISM/BARBARISM)
    if (policyId === POLICIES.AGRARIANISM
      && isPlayerFaction(currentPlayer, FACTIONS.RATMEN)
      && !isRatmenAgrarianismBoostActive
    ) {
      return 3;
    }

    // HEGEMONISM дает бонусы только в центре карты: без своего присутствия там
    // (квартал или отряд в бонусной зоне) политика бесполезна — не покупаем
    if (policyId === POLICIES.HEGEMONISM) {
      const ownQuarters = getOwnQuarterHexKeys(selectCitiesCache(state), currentPlayer.color);
      const hasCentralQuarterPresence = [...ownQuarters.keys()].some(isHegemonismBonusHexKey);
      const hasCentralUnitPresence = selectUnits(state).some(
        (unit) => unit.ownerId === currentPlayer.id && isHegemonismBonusHexKey(unit.hexKey)
      );
      if (!hasCentralQuarterPresence && !hasCentralUnitPresence) {
        return 0;
      }
      // Волна 23 (22.2): свои руины в радиусе 1 от центра — крепкое основание
      // гегемонистской стратегии (кварталы/храмы в зоне, армия в центр)
      if (isHegemonismCentralRuinsCase) {
        value += HEGEMONISM_CENTRAL_RUINS_BOOST;
      }
    }

    return value;
  };

  // Смена активной политики — выброшенные деньги, если новая не заметно лучше.
  // Люди — исключение: скидка 3 монеты делает первые три слота рынка бесплатными,
  // частая смена политик — их фракционная идентичность, а не потери
  // (сравнение релевантности ТЕКУЩЕЙ политики уже входит в модель ценности)
  const replacementThreshold = isHumans ? 1 : POLICY_REPLACEMENT_THRESHOLD;
  const currentPolicyValue = currentPlayer.policy
    ? calcPolicyValue(currentPlayer.policy.id) + replacementThreshold
    : 0;

  // Волна 36 (PT): цель из топ-3 бот золото+ берет и из дорогого слота — лишь бы
  // хватило тратимых монет и она была ценнее действующей с порогом замены, — и
  // берет ее раньше любой не-цели того же рынка. Только пока цели еще нет:
  // смена одной цели на другую идет по обычному правилу «ценность выше цены».
  // Политика топ-3, которую бот сам не купил бы, целью не считается
  const isGoldTargetPriceWaived = isGoldTargetsActive
    && !(currentPlayer.policy && goldTopPolicies.includes(currentPlayer.policy.id)
      && checkIfPolicyWhitelisted(currentPlayer.policy.id));
  // Замена (политика уже действует) вступает у не-людей с конца раунда: цель
  // берется в обход цены, только если и с этой поправкой она ценнее
  // действующей с порогом замены — та же мерка, что у обновления ради замены (PX)
  const goldTargetReplacementShare = isGoldTargetPriceWaived && currentPlayer.policy
    ? calcReplacementEffectShare(state, currentPlayer)
    : 1;

  let bestIndex = -1;
  // -Infinity, а не 0: порог «выгодности» вынесен в явную проверку ниже, где
  // его и ослабляет noPolicyRelief. При relief = 0 условие прежнее — score > 0
  let bestScore = -Infinity;
  let isBestGoldTarget = false;
  // Все слоты, прошедшие гейты покупки (лучший из них — bestIndex): по ним
  // обновление рынка волны 36 отличает «купить нечего» от «купить можно только
  // не-цель»
  const buyableIndices = [];

  policyMarket.forEach((policy, index) => {
    if (!policy) return;
    if (currentPlayer.policy && currentPlayer.policy.id === policy.id) {
      return; // уже активна такая же
    }

    // Запреты покупки (фора новичкам, ранние раунды партии) — до всякой
    // оценки: до вайтлиста (у фракций без него идут прежние предпочтения),
    // ситуативных бустов и послабления «засиделся без политики». Слот
    // отсекается здесь, а не нулевой ценностью в calcPolicyValue: той же
    // функцией выше оценена ДЕЙСТВУЮЩАЯ политика, и запрет покупки обесценил
    // бы уже купленную — движок сменил бы ее на что попало. Купить ее мог не
    // он: ИИ-игрок до ухода на автопилот, админ при ручном управлении ботом
    if (forbiddenPolicies.includes(policy.id)) {
      return;
    }

    const price = calcPolicyPrice(index, currentPlayer);
    if (price > spendableCoins) {
      return;
    }

    const value = calcPolicyValue(policy.id, price);
    // Этот гейт НЕ ослабляется: нулевую ценность возвращают вайтлист волны 30
    // и анти-список волны 24 — переплачивать за запрещенную фракции политику
    // бот не должен ни в каком раунде (волна 27: -6.9 п.п. винрейта)
    if (value <= currentPolicyValue) {
      return; // не лучше текущей — не меняем
    }

    const score = value - currentPolicyValue - price;
    const isGoldTarget = isGoldTargetPriceWaived && goldTopPolicies.includes(policy.id)
      && value * goldTargetReplacementShare > currentPolicyValue;
    // Обычно покупка требует score > 0 (ценность выше цены слота). Бот без
    // политики с 4-го раунда постепенно допускает переплату
    if (!isGoldTarget && score + noPolicyRelief <= 0) {
      return;
    }
    buyableIndices.push(index);

    // Без подчасти PT isGoldTarget всегда false — сравнение прежнее, по score
    if (isGoldTarget !== isBestGoldTarget ? isGoldTarget : score > bestScore) {
      bestScore = score;
      bestIndex = index;
      isBestGoldTarget = isGoldTarget;
    }
  });

  return {
    blockReason: null,
    bestIndex,
    buyableIndices,
    spendableCoins,
    forbiddenPolicies,
    calcPolicyValue,
    checkIfPolicyWhitelisted,
    currentPolicyValue,
    goldTopPolicies: isGoldTargetsActive ? goldTopPolicies : null,
  };
};

/**
 * Какую политику рынка бот текущего хода купил бы сейчас: индекс слота рынка
 * или -1. Только оценка, без покупки и без записи в стратегию бота — ее
 * спрашивают и заранее (темп хода сетевого бота, см.
 * online/constructionTurnPacing).
 */
export const findBotPolicyToBuy = (state) => evaluateBotPolicyMarket(state).bestIndex;

/**
 * Волна 36 (боты золото+): зачем бот текущего хода обновил бы рынок политик
 * прямо сейчас — причина из W36_MARKET_REFRESH_REASONS или null. Только оценка:
 * ничего не диспатчит и в стратегию бота не пишет — ее спрашивают и заранее
 * (темп хода сетевого бота: желание обновить рынок придерживается, как покупка
 * политики, см. executeBotStep).
 *
 * Цели — топ-3 фракции (getW36GoldTopPolicies). Цель «жизнеспособна», если
 * бот купил бы ее хотя бы в идеале: ценность при цене null больше нуля (это
 * отсекает вайтлист и анти-списки, Фанатизм без руин, Гегемонизм без центра,
 * Фортификационизм без угрозы), она не под запретом покупки (фора новичкам,
 * ранний запрет Экоанархизма) и это не действующая политика.
 *
 * Подчасти (каждая за своим legacy-гейтом A/B):
 *  - PR (NO_POLICY): политики нет, купить с рынка нечего, а жизнеспособна цель
 *    из топ-3 или хотя бы какая-то политика; раунд ≤ getW36MarketRefreshLastRound;
 *  - PW (OVER_WEAK): политики нет, купить можно только не-цель, а цель из топ-3
 *    жизнеспособна; раунд ≤ getW36RefreshOverWeakLastRound. Покупку при этом
 *    придерживает decidePolicyStep; после обновления (флаг раунда стоит) бот
 *    покупает как раньше;
 *  - PX (REPLACE): действующая политика не цель (не из топ-3 или из топ-3, но
 *    бот сам ее не купил бы — вне вайтлиста), купить нечего, а цель из
 *    топ-3 с поправкой на позднее вступление замены (calcReplacementEffectShare)
 *    ценнее действующей с порогом замены; раунд ≤ getW36PolicyReplaceLastRound.
 *
 * Общие условия — проверки кнопки обновления (PolicyMarketClearButton; редьюсер
 * на нарушения только пишет console.error): фаза строительства, рынок в этом
 * раунде еще не обновлялся (isClearedPolicyMarketThisTurn), есть действие. Плюс
 * свои: не песочница (сильный режим, rollout планировщика — будущий рынок там
 * выдуман, а Math.random только шумит), не последний раунд, политика в этой
 * фазе еще не куплена, начало хода (построек в pending нет), после обновления
 * остается не меньше W36_MARKET_REFRESH_MIN_ACTIONS_LEFT действий, тратимых
 * монет хватает на слот W36_MARKET_REFRESH_MIN_AFFORDABLE_SLOT_INDEX.
 *
 * evaluation — уже посчитанная evaluateBotPolicyMarket(state) того же
 * состояния (decidePolicyStep), чтобы не считать оценку дважды.
 */
export const findBotPolicyMarketRefreshReason = (state, {isSandbox = false, evaluation = null} = {}) => {
  if (isSandbox) {
    return null;
  }
  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer || selectPhase(state) !== PHASES.CONSTRUCTION) {
    return null;
  }

  // Дешевые проверки до всякой оценки: у ботов не золото+ и у legacy-групп A/B
  // дальше не считается ничего
  const round = selectRound(state);
  const gameType = selectGameType(state);
  const hasPolicy = Boolean(currentPlayer.policy);
  const isNoPolicyRefreshOn = !hasPolicy && round <= getW36MarketRefreshLastRound()
    && checkIfW36Feature(currentPlayer, gameType, isPreW36MarketRefreshPlayer);
  const isOverWeakRefreshOn = !hasPolicy && round <= getW36RefreshOverWeakLastRound()
    && checkIfW36Feature(currentPlayer, gameType, isPreW36RefreshOverWeakPlayer);
  const isReplaceRefreshOn = hasPolicy && round <= getW36PolicyReplaceLastRound()
    && checkIfW36Feature(currentPlayer, gameType, isPreW36PolicyReplacePlayer);
  if (!isNoPolicyRefreshOn && !isOverWeakRefreshOn && !isReplaceRefreshOn) {
    return null;
  }

  if (currentPlayer.nextPolicy || currentPlayer.isClearedPolicyMarketThisTurn || checkIfFinalRound(state)) {
    return null;
  }
  const pendingBuildings = selectPendingBuildings(state);
  if (pendingBuildings.length > 0) {
    return null;
  }
  const usedActions = calcUsedConstructionActions(
    currentPlayer, pendingBuildings,
    selectNextInitiativePlayerId(state), selectIsInitiativeInterceptedWithIsolationism(state)
  );
  // Само обновление тратит одно действие
  if ((currentPlayer.constructionPhaseActions || 0) - usedActions - 1 < W36_MARKET_REFRESH_MIN_ACTIONS_LEFT) {
    return null;
  }

  const marketEvaluation = evaluation || evaluateBotPolicyMarket(state);
  if (marketEvaluation.blockReason) {
    return null;
  }
  if (marketEvaluation.spendableCoins
    < calcPolicyPrice(W36_MARKET_REFRESH_MIN_AFFORDABLE_SLOT_INDEX, currentPlayer)
  ) {
    return null;
  }

  const {
    calcPolicyValue, checkIfPolicyWhitelisted, forbiddenPolicies, buyableIndices, currentPolicyValue,
  } = marketEvaluation;
  const checkIfViablePolicy = (policyId) => policyId !== currentPlayer.policy?.id
    && !forbiddenPolicies.includes(policyId)
    && calcPolicyValue(policyId, null) > 0;
  const topPolicies = getW36GoldTopPolicies(currentPlayer.factionId);
  const viableTopPolicies = topPolicies.filter(checkIfViablePolicy);

  if (buyableIndices.length === 0) {
    if (isNoPolicyRefreshOn
      && (viableTopPolicies.length > 0 || POLICY_IDS_IMPLEMENTED.some(checkIfViablePolicy))
    ) {
      return W36_MARKET_REFRESH_REASONS.NO_POLICY;
    }
    const isHoldingTarget = hasPolicy && topPolicies.includes(currentPlayer.policy.id)
      && checkIfPolicyWhitelisted(currentPlayer.policy.id);
    if (isReplaceRefreshOn && !isHoldingTarget) {
      const effectShare = calcReplacementEffectShare(state, currentPlayer);
      if (viableTopPolicies.some((policyId) => calcPolicyValue(policyId, null) * effectShare > currentPolicyValue)) {
        return W36_MARKET_REFRESH_REASONS.REPLACE;
      }
    }
    return null;
  }

  // Купить можно, но только не-цели: ни один доступный к покупке слот не из
  // топ-3 (у бота с подчастью PT цель на рынке и так выбирается первой)
  const policyMarket = selectPolicyMarket(state);
  if (isOverWeakRefreshOn
    && viableTopPolicies.length > 0
    && buyableIndices.every((index) => !topPolicies.includes(policyMarket[index].id))
  ) {
    return W36_MARKET_REFRESH_REASONS.OVER_WEAK;
  }
  return null;
};

/** Хочет ли бот текущего хода обновить рынок политик прямо сейчас (см. findBotPolicyMarketRefreshReason) */
export const checkIfBotWantsToRefreshPolicyMarket = (state, {isSandbox = false} = {}) => (
  findBotPolicyMarketRefreshReason(state, {isSandbox}) !== null
);

/** Хочет ли бот текущего хода купить политику прямо сейчас (см. findBotPolicyToBuy) */
export const checkIfBotWantsToBuyPolicy = (state) => findBotPolicyToBuy(state) !== -1;

/**
 * Покупка политики на рынке (не тратит действие строительства, максимум 1 за фазу):
 * слот — по оценке findBotPolicyToBuy.
 */
export const decidePolicyStep = (store) => {
  const state = store.getState();
  const evaluation = evaluateBotPolicyMarket(state);
  const {bestIndex} = evaluation;
  if (bestIndex === -1) {
    return false;
  }

  // Волна 36 (PW, боты золото+): без политики купить можно только не-цель, а
  // цель из топ-3 жизнеспособна — сначала обновление рынка (его делает
  // decidePolicyMarketRefreshStep этим же тиком), покупка — уже с нового рынка.
  // У остальных ботов причина обновления — всегда null
  if (findBotPolicyMarketRefreshReason(state, {isSandbox: Boolean(store.__isBotSandbox), evaluation})
    === W36_MARKET_REFRESH_REASONS.OVER_WEAK
  ) {
    return false;
  }

  const currentPlayer = selectCurrentPlayer(state);
  const policyPurchases = getBotStrategy(currentPlayer.id).policyPurchases || 0;

  // Точное повторение последовательности покупки из PolicyMarketSideMenu
  store.dispatch(buyPolicy(bestIndex));
  store.dispatch(addNewPolicyToPolicyMarket(getRandomPolicyForMarket()));
  getBotStrategy(currentPlayer.id).policyPurchases = policyPurchases + 1;

  return true;
};

/**
 * Волна 36 (боты золото+): обновление рынка политик (CLEAR_POLICY_MARKET) —
 * тратит действие строительства, раз в раунд. Решение — по
 * findBotPolicyMarketRefreshReason; на следующем тике decidePolicyStep выбирает
 * из нового рынка. Исполнение — ровно как кнопка PolicyMarketClearButton:
 * новый рынок генерирует тот, кто обновляет, и он едет в payload экшена
 * (реплей детерминирован). Счетчик policyMarketRefreshes в стратегии бота — для
 * метрик; «уже обновлял в этом раунде» решает флаг redux
 * (isClearedPolicyMarketThisTurn).
 */
export const decidePolicyMarketRefreshStep = (store) => {
  const state = store.getState();
  const reason = findBotPolicyMarketRefreshReason(state, {isSandbox: Boolean(store.__isBotSandbox)});
  if (!reason) {
    return false;
  }

  const currentPlayer = selectCurrentPlayer(state);
  const policyMarketBefore = selectPolicyMarket(state);
  const policyMarket = getNewPolicyMarket();
  store.dispatch(clearPolicyMarket(policyMarket));

  const strategy = getBotStrategy(currentPlayer.id);
  strategy.policyMarketRefreshes = (strategy.policyMarketRefreshes || 0) + 1;

  // Журнал харнесса (в проде не задан): причина и рынок до/после
  if (Array.isArray(globalThis.__w36PolicyMarketRefreshLog)) {
    globalThis.__w36PolicyMarketRefreshLog.push({
      round: selectRound(state),
      playerId: currentPlayer.id,
      factionId: currentPlayer.factionId,
      reason,
      policyId: currentPlayer.policy?.id || null,
      marketBefore: (policyMarketBefore || []).map((policy) => policy?.id || null),
      marketAfter: policyMarket.map((policy) => policy?.id || null),
    });
  }

  return true;
};
